import { useRef, useState, useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Share2, Download, Copy, X, Check } from "lucide-react";
import {
  FORMAT_DIMENSIONS,
  generateCardBlob,
  copyImageToClipboard,
  downloadBlob,
} from "../../lib/socialCard";
import type { SocialCardFormat } from "../../lib/socialCard";
import { SocialCard } from "./SocialCard";

// ── Types ─────────────────────────────────────────────────────────────────────

interface Props {
  label: string;
  title: string;
  cardContent: (format: SocialCardFormat) => ReactNode;
}

// ── Format labels ─────────────────────────────────────────────────────────────

const FORMAT_META: Record<SocialCardFormat, { label: string; sub: string }> = {
  square: { label: "Quadrat", sub: "1080×1080" },
  landscape: { label: "Landscape", sub: "1920×1080" },
  story: { label: "Story", sub: "1080×1920" },
};

const FORMATS: SocialCardFormat[] = ["square", "landscape", "story"];

// ── Modal content ─────────────────────────────────────────────────────────────

interface ModalProps {
  title: string;
  cardContent: (format: SocialCardFormat) => ReactNode;
  onClose: () => void;
}

function ShareModal({ title, cardContent, onClose }: ModalProps) {
  const [format, setFormat] = useState<SocialCardFormat>("square");
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [clipboardSupported, setClipboardSupported] = useState(true);
  const cardRef = useRef<HTMLDivElement | null>(null);

  // Check clipboard support on mount (async test)
  useEffect(() => {
    if (
      typeof navigator === "undefined" ||
      !navigator.clipboard ||
      typeof ClipboardItem === "undefined"
    ) {
      setClipboardSupported(false);
    }
  }, []);

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Prevent body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const { width, height } = FORMAT_DIMENSIONS[format];

  // Scale the preview to fit within a max preview area
  const MAX_PREVIEW_W = 480;
  const MAX_PREVIEW_H = 360;
  const scale = Math.min(MAX_PREVIEW_W / width, MAX_PREVIEW_H / height, 1);

  async function getBlob() {
    if (!cardRef.current) throw new Error("Card ref not mounted");
    return generateCardBlob(cardRef.current, format);
  }

  async function handleDownload() {
    setGenerating(true);
    try {
      const blob = await getBlob();
      const timestamp = new Date()
        .toISOString()
        .slice(0, 16)
        .replace(/[T:]/g, "-");
      downloadBlob(blob, `reasonz-${format}-${timestamp}.png`);
    } finally {
      setGenerating(false);
    }
  }

  async function handleCopy() {
    setGenerating(true);
    try {
      const blob = await getBlob();
      const ok = await copyImageToClipboard(blob);
      if (ok) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        setClipboardSupported(false);
      }
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[9000] flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(17,17,19,0.60)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Social Card teilen"
    >
      <div className="bg-rc-paper rounded-2xl shadow-cardLg w-full max-w-xl flex flex-col overflow-hidden max-h-[90dvh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-rc-fog flex-shrink-0">
          <span className="text-[15px] font-semibold text-rc-black">Teilen</span>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-rc-fog hover:bg-rc-stone/30 flex items-center justify-center transition-colors"
            aria-label="Schließen"
          >
            <X className="w-4 h-4 text-rc-charcoal" />
          </button>
        </div>

        {/* Preview */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div
            className="flex items-center justify-center bg-rc-cream rounded-xl overflow-hidden mb-5"
            style={{ minHeight: 200 }}
          >
            <div
              style={{
                width: width * scale,
                height: height * scale,
                overflow: "hidden",
                flexShrink: 0,
                borderRadius: 8,
                boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
              }}
            >
              <div
                style={{
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                  width,
                  height,
                  pointerEvents: "none",
                }}
              >
                <SocialCard
                  format={format}
                  title={title}
                  content={cardContent(format)}
                  generatedAt={new Date()}
                />
              </div>
            </div>
          </div>

          {/* Format toggle */}
          <div className="mb-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-rc-slate mb-3">
              Format
            </p>
            <div className="flex gap-2 flex-wrap">
              {FORMATS.map((f) => {
                const meta = FORMAT_META[f];
                const active = f === format;
                return (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setFormat(f)}
                    className={`flex flex-col items-start px-4 py-3 rounded-xl border text-left transition-colors ${
                      active
                        ? "bg-rc-berry text-rc-paper border-rc-berry"
                        : "bg-rc-cream border-rc-fog text-rc-charcoal hover:border-rc-stone"
                    }`}
                  >
                    <span className="text-[13px] font-semibold leading-tight">{meta.label}</span>
                    <span
                      className={`text-[11px] leading-tight mt-0.5 tabular-nums ${
                        active ? "text-rc-paper/70" : "text-rc-slate"
                      }`}
                    >
                      {meta.sub}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 px-6 py-5 border-t border-rc-fog flex-shrink-0">
          <button
            type="button"
            onClick={handleDownload}
            disabled={generating}
            className="flex items-center gap-2 bg-rc-berry text-rc-paper rounded-pill px-5 py-2.5 text-[13px] font-semibold hover:bg-rc-berry-700 transition-colors disabled:opacity-60"
          >
            <Download className="w-4 h-4" />
            {generating ? "Wird erstellt…" : "Download PNG"}
          </button>

          {clipboardSupported && (
            <button
              type="button"
              onClick={handleCopy}
              disabled={generating}
              className="flex items-center gap-2 bg-rc-paper border border-rc-fog text-rc-charcoal rounded-pill px-5 py-2.5 text-[13px] font-semibold hover:bg-rc-fog transition-colors disabled:opacity-60"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-rc-success" />
                  Kopiert
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  In Zwischenablage
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Off-screen native-size card for rasterisation */}
      <div
        style={{
          position: "fixed",
          top: -10000,
          left: -10000,
          pointerEvents: "none",
          zIndex: -1,
        }}
        aria-hidden="true"
      >
        <div ref={cardRef}>
          <SocialCard
            format={format}
            title={title}
            content={cardContent(format)}
            generatedAt={new Date()}
          />
        </div>
      </div>
    </div>
  );
}

// ── Public component ──────────────────────────────────────────────────────────

export function ShareButton({ label, title, cardContent }: Props) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={label}
        title={label}
        className="flex items-center gap-1.5 bg-rc-paper border border-rc-fog text-rc-charcoal rounded-full px-3 py-2 text-[12px] font-medium hover:bg-rc-fog transition-colors"
      >
        <Share2 className="w-3.5 h-3.5" />
        Teilen
      </button>

      {open &&
        createPortal(
          <ShareModal
            title={title}
            cardContent={cardContent}
            onClose={() => setOpen(false)}
          />,
          document.body,
        )}
    </>
  );
}
